import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Row, Col, Form, Button, Alert, Spinner, Badge, Dropdown } from "react-bootstrap";
import {
  FaCheckCircle, FaExclamationTriangle, FaTrash, FaPen, FaDownload,
  FaCloudUploadAlt, FaFilePdf, FaArrowRight, FaArrowLeft, FaFileInvoiceDollar,
  FaRegClock, FaUserCircle,
} from "react-icons/fa";
import { authHeaders } from "./QuoteCreate";
import { downloadQuoteReport, REPORT_STYLES } from "./utils/quoteReports";
import { PO_CLASS, classifyPo } from "./utils/poClassification";
import sessionStorage from "./utils/sessionStore";
import { hasAction } from "./utils/auth";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// Placeholder <option> value for the Ship-To picker. Epicor's main-office ship-to
// has ShipToNum === "", so the empty string is a legitimate selection and cannot
// be reused as the "not chosen" marker — that made the main office impossible to
// pick (it read as unselected, so the step gates never opened).
const SHIPTO_UNSET = "__unset__";

// -----------------------------------------------------------------------------
// Local formatters (kept self-sufficient — mirrors QuoteEdit's philosophy).
// -----------------------------------------------------------------------------
function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

// Short, friendly actor for the reviewer chain (email local-part; worker → "system").
function shortActor(a) {
  if (!a) return "—";
  if (String(a).toLowerCase().startsWith("system:")) return "system";
  const at = String(a).indexOf("@");
  return at > 0 ? a.slice(0, at) : a;
}
// Compact local time for an ISO/UTC timestamp from the review event log.
function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Map an Epicor terms code to one of the four plan buckets.
function decodeTermsPlan(termsCode) {
  const c = String(termsCode || "").trim().toUpperCase();
  if (!c) return "N30";
  if (c[0] === "B") return "BK";
  if (c[0] === "P") return "POT";
  if (c[0] === "C") return "CTO";
  return "N30";
}

// Normalize coverage.planCategory (CASH/SPLIT/POT/CTO) or a terms code into a
// short plan key, a human label, and a coverage-strip color.
function planMeta(coverage, termsCode) {
  const planKey = decodeTermsPlan(termsCode);                          // N30 | BK | POT | CTO
  const cat = (coverage?.planCategory
    || ({ N30: "CASH", BK: "SPLIT", POT: "POT", CTO: "CTO" }[planKey]))
    || "UNKNOWN";
  const label = coverage?.planLabel
    || ({ CASH: "Net 30", SPLIT: "Balanced Split", POT: "Pay-Over-Time", CTO: "Consign-to-Own" }[cat])
    || (termsCode || "Unknown");
  const pillClass = { N30: "qw-pill-emerald", BK: "qw-pill-indigo", POT: "qw-pill-violet", CTO: "qw-pill-amber" }[planKey] || "qw-pill-slate";
  return { planKey, cat, isCash: cat === "CASH", label, pillClass };
}

const STEPS = ["Upload POs", "Contacts", "Review", "Status"];

// QuoteReview lifecycle statuses that are still in flight — the status tracker
// keeps polling while the review sits in one of these.
const NON_TERMINAL_STATUSES = ["PendingReview", "Approved", "Creating"];

// statusKind (from the API's QuoteReviewView) → a Bootstrap-ish accent color.
function statusColor(kind) {
  return { info: "#0052A5", progress: "#6366F1", success: "#10B981", warning: "#B45309", error: "#DC2626" }[kind] || "#64748B";
}

// PO extraction / hold classification lives in a shared util so the review queue
// can roll each quote's held POs up into the same error-status categories.

// Tracks a phone-width viewport (Bootstrap's `sm` breakpoint). Drives the
// compact step rail + full-width footer buttons so the flow is thumb-friendly
// on mobile web / the installed PWA.
function useIsMobile() {
  const query = "(max-width: 575.98px)";
  const read = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  const [isMobile, setIsMobile] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", onChange); else mq.removeListener(onChange); };
  }, []);
  return isMobile;
}

// =============================================================================
// ConvertToOrderModal — guided Quote → Sales Order conversion.
//
// Fits the existing QuoteEdit call site:  <ConvertToOrderModal show quote
// onConverted onHide />.  Replaces the old single-screen manual-PO modal with
// the live-traced flow from QuoteToSO_UI_Simulator_v3 / the Complete Reference:
//
//   Step 1  Upload PO PDFs   → POST sas-token → PUT blob → POST {id}/confirm
//                              (server runs the OCR cascade and returns the
//                              embedded, plan-aware coverage block).  Per-PO
//                              floor + plan minimum drive the coverage strip.
//   Step 2  Pick contacts    → GET /api/CustCnt?custNum=…  (pre-selects the
//                              PrimaryBilling sold-to and PrimaryShipping ship-to).
//   Step 3  Review & Convert → POST /api/Quote/{n}/convert-to-order  (6-phase
//                              orchestrator: contact gate → drift → Epicor RPCs
//                              → enrich → PO map → ReadyToCalc).
//   Step 4  Done             → order number + "Ack scheduled ~30s" (Hangfire).
//
// Per-plan coverage rules are enforced by the backend; this UI surfaces the
// coverage strings (minimumDescription / currentMessage / remainingDescription)
// verbatim and never hand-rolls coverage prose.
// =============================================================================
function ConvertToOrderModal({ show, onHide, quote, onConverted }) {
  const navigate = useNavigate();
  const quoteNum = quote?.quoteNum;
  const custNum  = quote?.custNum ?? quote?.custID;   // QuoteDTO carries custNum
  const quoteShipTo = String(quote?.shipToNum ?? "").trim();
  const confirmedBy = sessionStorage.getItem("email") || sessionStorage.getItem("salesRepCode") || undefined;
  const isMobile = useIsMobile();

  const [step, setStep] = useState(0);

  // Coverage + plan
  const [coverage, setCoverage]               = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // Uploaded / confirmed PO attachments + optional manual PO rows
  const [attachments, setAttachments] = useState([]);   // confirmed PO rows (have attachmentId)
  const [uploading, setUploading]     = useState(false);
  const [dragActive, setDragActive]   = useState(false);  // drop-zone hover state
  const [uploadErr, setUploadErr]     = useState(null);
  const [editId, setEditId]           = useState(null);  // attachmentId being inline-edited
  const [editVals, setEditVals]       = useState({ poNumber: "", poAmount: "" });
  const fileInputRef = useRef(null);

  // Contacts — Sold-To (main office) and Ship-To Attn are SEPARATE lists.
  // Epicor scopes a contact number by (CustNum, ShipToNum): the same ConNum is a
  // different person at a different ship-to, and a Sold-To (main-office) contact
  // is NOT a valid Ship-To Attn. So each dropdown loads from its own CustCnt
  // query — Sold-To with shipToNum="" (main office), Ship-To Attn filtered by the
  // chosen location — which makes an invalid ShpConNum impossible to pick (the
  // "Invalid Ship To Con Num" convert failure). See docs/UI_Contact_Selection_OrderCreation.md.
  const [soldToContacts, setSoldToContacts]   = useState([]);
  const [soldToLoading, setSoldToLoading]     = useState(false);
  const [shipToContacts, setShipToContacts]   = useState([]);         // members of the selected ship-to
  const [shipToContactsLoading, setShipToContactsLoading] = useState(false);
  const [prcConNum, setPrcConNum]             = useState("");   // Sold-To contact (CustCnt.ConNum)
  const [shpConNum, setShpConNum]             = useState("");   // Ship-To attention contact (CustCnt.ConNum)

  // Ship-To LOCATIONS (the rep picks the location; the backend resolves its email
  // to CC on the acknowledgement and stamps the order). #4 — a real location, not a contact.
  // Holds only SELECTABLE locations: every customer also carries an Epicor
  // ShipToNum "" row mirroring the main office, which is not a delivery location
  // — its CustCnt members are the Sold-To contacts, so offering it fed sold-to
  // people into the Ship-To Attn list. Filtered out at the fetch below.
  const [shipTos, setShipTos]                 = useState([]);
  // null is the "nothing picked yet" sentinel and "0" is a REAL ship-to that
  // several customers own, so every gate below tests `=== null` / `!== null`,
  // never emptiness — or ship-to "0" becomes unselectable.
  const [shipToNum, setShipToNum]             = useState(null);

  // Submit-for-review + status tracking
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr]   = useState(null);   // { code, message, extra }
  // Invoice / billing recipient section state — marking a contact as billing
  // happens on the CUSTOMER screen (opened in a new tab); here we only re-check.
  const [recheckingBilling, setRecheckingBilling] = useState(false);
  const [review, setReview]         = useState(null);   // QuoteReviewView (submit result + polling)
  const [successMsg, setSuccessMsg] = useState(null);   // toast handed to parent on close
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState(null);

  // Download the quote report PDF — same authenticated blob download the quote view
  // offers, made available here once the order has been created.
  const handleDownload = async (variant) => {
    if (!quoteNum) return;
    setDownloading(true); setDownloadErr(null);
    try {
      await downloadQuoteReport(quoteNum, variant);
    } catch (e) {
      setDownloadErr(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const { planKey, isCash, label: planLabel, pillClass } = planMeta(coverage, quote?.termsCode);

  // ── Coverage fetch helper ──────────────────────────────────────────────────
  const refetchCoverage = useCallback(async () => {
    if (!quoteNum) return;
    setCoverageLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/Quote/${encodeURIComponent(quoteNum)}/coverage`, { headers: authHeaders() });
      const data = await r.json().catch(() => null);
      if (r.ok && data) setCoverage(data);
    } catch { /* coverage is advisory — non-fatal */ }
    finally { setCoverageLoading(false); }
  }, [quoteNum]);

  // ── On open: reset, then load coverage + existing attachments + contacts ────
  useEffect(() => {
    if (!show) return;
    setStep(0);
    setCoverage(null); setAttachments([]);
    setUploading(false); setUploadErr(null); setEditId(null);
    setSoldToContacts([]); setShipToContacts([]); setPrcConNum(""); setShpConNum("");
    setShipTos([]); setShipToNum("");
    setSubmitting(false); setSubmitErr(null); setReview(null);

    let cancelled = false;

    // Coverage
    refetchCoverage();

    // Existing PO attachments (resume support — rep may have uploaded on a
    // previous day). Load ALL non-deleted rows, including HELD ones (no confirmed
    // amount: low-confidence read, decoy total, or a part that doesn't match the
    // quote). The rep needs to see a held PO to fix it (enter PO#/amount) or delete
    // it — hiding it left the conversion stuck on a Hold the rep couldn't resolve.
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/Attachment?quoteNum=${encodeURIComponent(quoteNum)}`, { headers: authHeaders() });
        const list = await r.json().catch(() => []);
        if (cancelled || !Array.isArray(list)) return;
        const rows = list.map((a) => ({
          attachmentId: a.id,
          fileName:     a.fileName,
          poNumber:     a.confirmedPONumber || a.extractedPONumber || "",
          poAmount:     a.confirmedPOAmount ?? a.extractedPOAmount ?? 0,
          held:         a.confirmedPOAmount == null,
          confidence:   a.extractionConfidence,
          method:       a.extractionMethod,
          status:       a.extractionStatus,
          message:      a.extractionError || null,   // actual backend reason (reconcile/OCR audit)
          isDuplicate:  a.isDuplicate === true,
          duplicateOf:  a.duplicateOfAttachmentId ?? null,
        }));
        setAttachments(rows);
      } catch { /* resume is best-effort */ }
    })();

    // Sold-To (main office) contacts — shipToNum="" returns the customer-level
    // contacts; the selected one becomes PrcConNum. Ship-To Attn contacts are
    // loaded separately by the effect below, keyed on the chosen location.
    if (custNum) {
      setSoldToLoading(true);
      (async () => {
        try {
          const r = await fetch(`${API_URL}/api/CustCnt?custNum=${encodeURIComponent(custNum)}&shipToNum=&inactive=false`, { headers: authHeaders() });
          const data = await r.json().catch(() => null);
          if (cancelled) return;
          const list = (data?.contacts || data || []).filter(Boolean);
          setSoldToContacts(list);
          // The order's contact person IS the primary billing contact — no
          // free pick. Nothing eligible → the step blocks below until the rep
          // sets one on the customer screen.
          const billing = list.find((c) => c.primaryBilling && (c.eMailAddress || "").includes("@"));
          if (billing) setPrcConNum(String(billing.conNum));
        } catch { /* picker degrades gracefully to manual entry */ }
        finally { if (!cancelled) setSoldToLoading(false); }
      })();

      // Ship-To LOCATIONS — the rep picks the location (drives the order's ship-to
      // + the acknowledgement CC). Pre-select the primary, else the first.
      // Only genuine delivery locations are offered: `inactive=false` drops
      // retired addresses server-side, and the blank-ShipToNum row is dropped
      // here — Epicor returns one per customer mirroring the main office, and it
      // rendered as an option with no number whose "members" are the Sold-To
      // contacts. It is the backend's implicit default anyway, so leaving it out
      // of the picker loses nothing.
      (async () => {
        try {
          const r = await fetch(`${API_URL}/api/ShipTo?custNum=${encodeURIComponent(custNum)}&inactive=false`, { headers: authHeaders() });
          const data = await r.json().catch(() => null);
          if (cancelled) return;
          // The API answers `shipTos`; `shipToes` was the old monolith's key and
          // reading only it left this picker empty (UI-QED-031).
          const list = (data?.shipTos || data?.ShipTos || data?.shipToes || data?.ShipToes || [])
            .filter(Boolean)
            .filter((s) => String(s.shipToNum ?? "").trim() !== "");
          setShipTos(list);
          // The quote's own ship-to first (what the rep set on Edit quote), then
          // the primary, then the first — now that this list actually loads.
          const primary = (quoteShipTo && list.find((s) => String(s.shipToNum).trim() === quoteShipTo))
            || list.find((s) => s.primaryShipTo) || list[0];
          if (primary) setShipToNum(String(primary.shipToNum));
        } catch { /* ship-to picker degrades to the backend's default lookup */ }
      })();
    }

    return () => { cancelled = true; };
  }, [show, quoteNum, custNum, quoteShipTo, refetchCoverage]);

  // ── Ship-To Attn contacts — reload whenever the ship-to LOCATION changes ─────
  // The Ship-To Attn dropdown must only ever offer contacts that belong to the
  // selected location (Epicor's (CustNum, ShipToNum, ConNum) scoping). Loading
  // here — keyed on shipToNum — guarantees that: a location change clears the
  // prior (now-invalid) selection and refetches the members of the new location.
  // Until a location is chosen, the list stays empty (dropdown disabled).
  useEffect(() => {
    if (!show || !custNum) return;
    // A location change invalidates any previously-picked Ship-To Attn.
    setShpConNum(""); setShipToContacts([]);
    if (shipToNum === null) return;   // no location yet → nothing to load
    let cancelled = false;
    setShipToContactsLoading(true);
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/CustCnt?custNum=${encodeURIComponent(custNum)}&shipToNum=${encodeURIComponent(shipToNum)}&inactive=false`,
          { headers: authHeaders() });
        const data = await r.json().catch(() => null);
        if (cancelled) return;
        const list = (data?.contacts || data || []).filter(Boolean);
        setShipToContacts(list);
        // Pre-select the location's primary shipping contact (else its first).
        const shipping = list.find((c) => c.primaryShipping) || list[0];
        if (shipping) setShpConNum(String(shipping.conNum));
      } catch { /* leave empty → the UI blocks with a "no contact on file" notice */ }
      finally { if (!cancelled) setShipToContactsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [show, custNum, shipToNum]);

  // ── PO upload — the 3-call SAS dance per file ───────────────────────────────
  const uploadOneFile = async (file) => {
    // 1) Allocate the attachment row + short-lived SAS URL.
    const sasRes = await fetch(`${API_URL}/api/Attachment/sas-token`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        sourceQuoteNum: Number(quoteNum),
        fileName:       file.name,
        attachmentType: "PODocument",
        contentType:    file.type || "application/pdf",
      }),
    });
    const sas = await sasRes.json().catch(() => null);
    if (!sasRes.ok || !sas?.success || !sas.sasUploadUrl) {
      throw new Error(sas?.error || `Could not allocate upload slot for ${file.name}.`);
    }

    // 2) Browser PUTs the bytes straight to Azure Blob (not via our API tier).
    const putRes = await fetch(sas.sasUploadUrl, {
      method: "PUT",
      headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/pdf" },
      body: file,
    });
    if (!putRes.ok) throw new Error(`Blob upload failed for ${file.name} (HTTP ${putRes.status}).`);

    // 3) Confirm — server downloads the blob, runs the OCR cascade, and returns
    //    the extracted PO fields + the recomputed plan-aware coverage block.
    const confRes = await fetch(`${API_URL}/api/Attachment/${sas.attachmentId}/confirm`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ fileSizeBytes: file.size }),
    });
    const conf = await confRes.json().catch(() => null);
    if (!confRes.ok || !conf?.success) {
      throw new Error(conf?.error || `Extraction failed for ${file.name}.`);
    }

    return {
      row: {
        attachmentId: conf.attachmentId,
        fileName:     conf.fileName || file.name,
        poNumber:     conf.confirmedPONumber || conf.extractedPONumber || "",
        poAmount:     conf.confirmedPOAmount ?? conf.extractedPOAmount ?? 0,
        held:         conf.confirmedPOAmount == null,
        confidence:   conf.extractionConfidence,
        method:       conf.extractionMethod,
        status:       conf.extractionStatus,
        message:      conf.extractionError || conf.error || null,   // actual backend reason (reconcile/OCR audit)
      },
      coverage: conf.coverage || null,
    };
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploadErr(null);
    setUploading(true);
    // One bad file must not stop the rest of the pick: each failure is collected and shown
    // in the red box once every file has been tried.
    const errors = [];
    try {
      for (const file of files) {
        try {
          const { row, coverage: cov } = await uploadOneFile(file);   // sequential — coverage is cumulative
          setAttachments((prev) => [...prev, row]);
          if (cov) setCoverage(cov);
        } catch (e) {
          errors.push(e.message);
        }
      }
      if (errors.length > 0) {
        setUploadErr(errors.join(" "));
        await refetchCoverage();
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── PO row actions: inline edit (manual OCR override), delete ───────────────
  const startEdit = (a) => { setEditId(a.attachmentId); setEditVals({ poNumber: a.poNumber, poAmount: String(a.poAmount ?? "") }); };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async (a) => {
    const poNumber = editVals.poNumber.trim();
    const poAmount = Number(editVals.poAmount);
    if (!poNumber || !(poAmount > 0)) { setUploadErr("Enter a PO number and an amount greater than 0."); return; }
    try {
      const r = await fetch(`${API_URL}/api/Attachment/${a.attachmentId}/confirm-po`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ poNumber, poAmount, confirmedBy }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok || data?.success === false) throw new Error(data?.message || data?.error || "Could not save the correction.");
      setAttachments((prev) => prev.map((x) => x.attachmentId === a.attachmentId ? { ...x, poNumber, poAmount, held: false, status: "Confirmed", message: null } : x));
      setEditId(null);
      if (data?.coverage) setCoverage(data.coverage); else await refetchCoverage();
    } catch (e) { setUploadErr(e.message); }
  };
  const deleteAttachment = async (a) => {
    try {
      const r = await fetch(`${API_URL}/api/Attachment/${a.attachmentId}`, { method: "DELETE", headers: authHeaders() });
      if (!r.ok && r.status !== 204) throw new Error(`Delete failed (HTTP ${r.status}).`);
      setAttachments((prev) => prev.filter((x) => x.attachmentId !== a.attachmentId));
      await refetchCoverage();
    } catch (e) { setUploadErr(e.message); }
  };

  // ── Derived gating ──────────────────────────────────────────────────────────
  const coverageMet  = coverage?.isMinimumMet === true;
  // N30 can always advance (the backend enforces the exact-match rule at convert
  // and surfaces N30_BALANCE_MISMATCH); BK/POT/CTO must hit the plan minimum first.
  const canLeaveUpload = isCash || coverageMet;

  // PO numbers that appear on more than one attachment — flagged as duplicates so
  // the rep removes the copy before it double-counts toward coverage. (G4)
  const duplicatePoNums = (() => {
    const counts = {};
    attachments.forEach((a) => {
      const n = (a.poNumber || "").trim();
      if (n) counts[n] = (counts[n] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((n) => counts[n] > 1));
  })();

  const selectedSoldTo   = soldToContacts.find((c) => String(c.conNum) === String(prcConNum));
  const selectedShipCon  = shipToContacts.find((c) => String(c.conNum) === String(shpConNum));
  const selectedShipTo   = shipToNum === null ? undefined : shipTos.find((s) => String(s.shipToNum) === String(shipToNum));
  // A ship-to LOCATION is chosen but its member-contact list came back empty —
  // block progress (never submit a Ship-To Attn that isn't a member of the ship-to).
  const shipToHasNoContacts = shipTos.length > 0 && shipToNum !== null
    && !shipToContactsLoading && shipToContacts.length === 0;
  // Both a Sold-To contact AND a Ship-To LOCATION + valid Ship-To Attn are required
  // (when the customer has any). The ship-to drives the destination + the ack CC.
  const contactOk = soldToContacts.length === 0 || String(prcConNum).trim() !== "";
  const shipToLocOk = shipTos.length === 0 || shipToNum !== null;
  // Ship-To Attn: once a location is chosen it must resolve to a valid member
  // contact — otherwise the order would fail Epicor's ship-to-contact stamp.
  const shipToAttnOk =
    shipTos.length === 0 ? true
    : shipToNum === null ? false
    : shipToContactsLoading ? false
    : shipToContacts.length === 0 ? false
    : String(shpConNum).trim() !== "";
  // ── Invoice / billing recipient (spec: InvoiceRecipient_UI_Integration_Spec) ──
  // The customer's ONE Primary Billing contact. They are the order's contact
  // person: stamped on the order at convert, and the recipient of the order
  // acknowledgement, invoices and purchase emails. The eligibility rule mirrors
  // the server exactly: primaryBilling === true AND eMailAddress contains "@".
  // Marking a contact happens on the CUSTOMER screen only — this modal blocks
  // and points there when nothing is eligible.
  const eligibleBilling = soldToContacts.filter(
    (c) => c.primaryBilling && (c.eMailAddress || "").includes("@"));
  const billingRecipient = eligibleBilling[0];
  // Required when the customer has contacts. (No contacts at all → the existing
  // add-contact path; the server returns its own "no contact" block on submit.)
  const billingOk = soldToContacts.length === 0 || eligibleBilling.length >= 1;

  const canLeaveContacts = contactOk && shipToLocOk && shipToAttnOk && billingOk;

  const refreshSoldToContacts = async () => {
    setRecheckingBilling(true);
    try {
      const r = await fetch(`${API_URL}/api/CustCnt?custNum=${encodeURIComponent(custNum)}&shipToNum=&inactive=false`, { headers: authHeaders() });
      const data = await r.json().catch(() => null);
      const list = (data?.contacts || data || []).filter(Boolean);
      setSoldToContacts(list);
      // Keep the contact person locked to the billing recipient.
      const billing = list.find((c) => c.primaryBilling && (c.eMailAddress || "").includes("@"));
      setPrcConNum(billing ? String(billing.conNum) : "");
    } finally { setRecheckingBilling(false); }
  };

  // Fixing the billing contact = a customer-screen job; open the customer in a
  // NEW tab so the conversion (uploaded POs, selections) stays right here.
  const openCustomerScreen = () =>
    window.open(`/customer/search?open=${encodeURIComponent(custNum)}`, "_blank", "noopener");

  // ── Submit for review (maker-checker) ───────────────────────────────────────
  // The rep no longer creates the order directly. Submitting hands the quote (with
  // its uploaded POs) to Customer Service. The POs are already linked to the quote
  // (uploaded by quoteNum), so the server recomputes coverage itself — we send only
  // the customer snapshot + the contact/ship-to selection.
  const submitForReview = async () => {
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const body = {
        customerName:  quote?.customerName || undefined,
        custID:        quote?.custID || undefined,
        salesRepCode:  quote?.salesRepCode || undefined,
        custNum:       Number(custNum) || undefined,
        contactConNum: String(prcConNum).trim() ? Number(prcConNum) : undefined,
        shipToNum:     shipToNum !== null ? String(shipToNum).trim() : undefined,
        shipToConNum:  String(shpConNum).trim() ? Number(shpConNum) : undefined,
      };
      const r = await fetch(`${API_URL}/api/QuoteReview/${encodeURIComponent(quoteNum)}/submit-for-review`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.quoteNum) {
        setSubmitErr({
          code:    data?.errorCode,
          message: data?.error || data?.message || `Submit failed (HTTP ${r.status}).`,
          extra:   data,
        });
        // Safety net (spec §4.3): a CONTACT_NOT_READY:InvoiceRecipient block
        // routes the rep back to the contacts step, where the billing section
        // shows exactly what to fix. The refusal banner goes WITH it (it was drawn
        // only on Review) and the billing contacts are re-read, so the section shows
        // the server's view, not the tick loaded when the window opened (UI-CNV-033).
        if (data?.errorCode === "CONTACT_NOT_READY"
            && Array.isArray(data?.warnings)
            && data.warnings.some((w) => String(w.code || "").endsWith(":InvoiceRecipient"))) {
          setSubmitErr((e) => ({ ...e, onContacts: true }));
          setStep(1);
          refreshSoldToContacts();
        }
        return;
      }
      setReview(data);
      setStep(3);
      setSuccessMsg({ type: "success", text: `Quote ${quoteNum} submitted for review.` });
    } catch (e) {
      setSubmitErr({ message: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Status polling — once submitted, watch the review move PendingReview →
  //    Approved → Creating → OrderCreated (or ChangesRequested / CreationFailed). ─
  useEffect(() => {
    if (step !== 3 || !review || !NON_TERMINAL_STATUSES.includes(review.status)) return;
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/QuoteReview/${encodeURIComponent(quoteNum)}`, { headers: authHeaders() });
        const data = await r.json().catch(() => null);
        if (cancelled || !r.ok || !data?.quoteNum) return;
        setReview(data);
        if (data.status === "OrderCreated") {
          setSuccessMsg({ type: "success", text: `Quote ${quoteNum} converted to sales order #${data.orderNum}.` });
        }
      } catch { /* transient — next tick retries */ }
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [step, review, quoteNum]);

  // ── Render helpers ──────────────────────────────────────────────────────────
  const StepRail = () => {
    // Mobile: dots + connectors only, with a single caption underneath — the
    // four word labels won't fit on a ~360px screen without wrapping.
    if (isMobile) {
      return (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div style={{
                  width: 18, height: 18, borderRadius: 999, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700,
                  background: i <= step ? "#0052A5" : "#E2E8F0", color: i <= step ? "#fff" : "#94A3B8",
                }}>{i < step ? "✓" : i + 1}</div>
                {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? "#0052A5" : "#E2E8F0", borderRadius: 2 }} />}
              </React.Fragment>
            ))}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A", marginTop: 6 }}>
            Step {Math.min(step + 1, STEPS.length)} of {STEPS.length} · {STEPS[step]}
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{
                width: 22, height: 22, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
                background: i < step ? "#0052A5" : i === step ? "#0052A5" : "#E2E8F0",
                color: i <= step ? "#fff" : "#94A3B8",
              }}>{i < step ? "✓" : i + 1}</div>
              <span style={{ fontSize: 12, fontWeight: i === step ? 700 : 500, color: i === step ? "#0F172A" : "#94A3B8" }}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? "#0052A5" : "#E2E8F0", borderRadius: 2 }} />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const CoverageStrip = () => {
    if (!coverage) {
      return coverageLoading
        ? <div style={{ fontSize: 13, color: "#64748B" }}><Spinner animation="border" size="sm" /> Checking PO coverage…</div>
        : null;
    }
    const total = Number(coverage.totalConfirmedAmount || 0);
    // The dollar line shows what the plan credited (a sub-floor PO that counts for nothing is
    // left out - OBS-45); the pill and the percent keep using the confirmed total.
    const credited = Number(coverage.creditedAmount ?? coverage.totalConfirmedAmount ?? 0);
    const due   = Number(coverage.totalDue || 0);
    const pct   = Math.max(0, Math.min(100, Number(coverage.percentCovered ?? (due > 0 ? (total / due) * 100 : 0))));
    const full  = coverage.isFullyCovered, min = coverage.isMinimumMet;
    // Over-covered: POs total more than the quote, beyond the $1 foot tolerance.
    // Without this branch the pill fell through to "Short" — backwards, since the
    // problem is an extra/duplicate PO, not a shortfall.
    const over = !full && !min && (total - due) > 1.00;
    const barColor = full ? "#10B981" : min ? "#0052A5" : over ? "#F59E0B" : "#EF4444";
    const unitLine = isCash
      ? "PO match"
      : `${coverage.unitsCovered ?? 0} of ${coverage.unitCount ?? 0} ${coverage.unitLabel || "invoice"}s`;
    const pill = total === 0
      ? <Badge bg="secondary">No POs</Badge>
      : full ? <Badge bg="success">Fully covered</Badge>
      : min  ? <Badge bg="primary">Minimum met</Badge>
      : over ? <Badge bg="warning" text="dark">Over — review</Badge>
      : <Badge bg="danger">Short</Badge>;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span className="qw-eyebrow">Coverage</span>{pill}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: 13.5, color: "#0F172A", whiteSpace: "nowrap" }}>{unitLine}</span>
          <span className="qw-mono" style={{ fontSize: 12, color: "#64748B", whiteSpace: "nowrap" }}>{fmtMoney(credited)}&nbsp;of&nbsp;{fmtMoney(due)}</span>
        </div>
        <div style={{ height: 6, background: "#EEF2F7", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 999, transition: "width .4s" }} />
        </div>
        {coverage.currentMessage && (
          <div style={{ fontSize: 12.5, color: min ? "#166534" : "#92400E", marginTop: 8, display: "flex", gap: 6, alignItems: "flex-start" }}>
            {min ? <FaCheckCircle style={{ marginTop: 2, flexShrink: 0 }} /> : <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />}
            <span>{coverage.currentMessage}</span>
          </div>
        )}
        {coverage.remainingDescription && !full && (
          // Warning tone (amber) while the minimum isn't met — the quote can't convert
          // yet; only fade to neutral once the gate is met (informational "later months…").
          <div style={{
            fontSize: 12, marginTop: 4,
            color: min ? "#64748B" : "#92400E",
            fontWeight: min ? 400 : 600,
          }}>
            {coverage.remainingDescription}
          </div>
        )}
      </div>
    );
  };

  // Invoice/month grid for BK/POT/CTO.
  const CoverageGrid = () => {
    if (!coverage || isCash || !(coverage.unitCount > 0)) return null;
    const covered = coverage.unitsCovered ?? 0;
    // Prefer the backend's SPECIFIC covered-unit list so we highlight exactly the
    // invoices/months a PO has covered (e.g. only #3). Fall back to "first N" when
    // the per-slot detail isn't available (amount-only fallback paths).
    const coveredSet = new Set(coverage.coveredUnits || []);
    const hasSpecific = coveredSet.size > 0;
    return (
      <div className="qw-card-flat qw-pad" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span className="qw-eyebrow">{coverage.unitLabel === "month" ? "Months" : "Invoices"}</span>
          <span style={{ fontSize: 12, color: "#64748B" }}>{covered} of {coverage.unitCount}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(38px,1fr))", gap: 5 }}>
          {Array.from({ length: coverage.unitCount }, (_, i) => {
            const isCovered = hasSpecific ? coveredSet.has(i + 1) : i < covered;
            return (
              <div key={i} style={{
                aspectRatio: "1", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
                background: isCovered ? "rgba(16,185,129,0.15)" : "#F1F5F9",
                color: isCovered ? "#047857" : "#94A3B8",
                border: isCovered ? "1px solid rgba(16,185,129,0.45)" : "1px solid transparent",
              }}>{i + 1}</div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Step 1: Upload ──────────────────────────────────────────────────────────
  const renderUpload = () => (
    <>
      <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
          <div>
            <div className="qw-eyebrow">Payment plan</div>
            <div style={{ fontWeight: 700, color: "#0F172A", marginTop: 2 }}>
              <span className={`qw-pill ${pillClass}`} style={{ marginRight: 8 }}>{planKey}</span>
              {planLabel} <span style={{ color: "#94A3B8", fontWeight: 500 }}>({quote?.termsCode || "—"})</span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="qw-eyebrow">Grand total</div>
            <div className="qw-mono" style={{ fontSize: 18, fontWeight: 700, color: "#0052A5" }}>
              {fmtMoney(coverage?.totalDue ?? quote?.quoteAmt)}
            </div>
          </div>
        </div>
        <CoverageStrip />
        {coverage?.minimumDescription && (
          <div style={{ fontSize: 12, color: "#64748B", marginTop: 8, paddingTop: 8, borderTop: "1px dashed #E2E8F0" }}>
            <strong>Required:</strong> {coverage.minimumDescription}
          </div>
        )}
      </div>

      <CoverageGrid />

      {/* Uploaded / confirmed POs */}
      {attachments.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Purchase orders ({attachments.length})</div>
          {/* Classification breakdown — counts per extraction outcome so CS sees the
              mix of holds at a glance (e.g. "1 Amount mismatch · 1 Part not matched"). */}
          {(() => {
            const counts = attachments.reduce((acc, a) => {
              const k = classifyPo(a);
              acc[k] = (acc[k] || 0) + 1;
              return acc;
            }, {});
            const order = ["confirmed", "mismatch", "partMiss", "noAmount", "planHold", "review", "failed", "duplicate"];
            const chips = order.filter((k) => counts[k]);
            return chips.length > 1 ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                {chips.map((k) => (
                  <Badge key={k} bg={PO_CLASS[k].tone} text={PO_CLASS[k].dark ? "dark" : undefined}>
                    {counts[k]} {PO_CLASS[k].label}
                  </Badge>
                ))}
              </div>
            ) : null;
          })()}
          {attachments.map((a) => {
            const editing = editId === a.attachmentId;
            // Actual backend reason. extractionError is a verbose audit trail —
            // "[N30 reconcile: Held] <reason> | <cascade summary>". The first segment
            // (before the raw cascade dump) is the human-facing message; surface it verbatim.
            const backendReason = (a.message || "").split("|")[0].trim();
            const clsMeta = PO_CLASS[classifyPo(a)];
            // Prefer the backend's authoritative per-attachment unitsCovered
            // (it accounts for rounding/remainder); fall back to a client-side
            // floor only when coverage hasn't returned a row for this PO yet.
            const covAtt = (coverage?.attachments || []).find((x) => x.attachmentId === a.attachmentId);
            const per = coverage && !isCash ? Number(coverage.perUnitAmount || 0) : 0;
            const units = isCash ? null
              : (covAtt && covAtt.unitsCovered != null) ? covAtt.unitsCovered
              : (per > 0 ? Math.floor(Number(a.poAmount) / per) : null);
            return (
              <div key={a.attachmentId} className="qw-card-flat" style={{ padding: 10, marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FaFilePdf style={{ color: "#B91C1C", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {!editing ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.fileName}</div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 2 }}>
                          <span className="qw-mono" style={{ fontSize: 11.5, color: "#64748B" }}>{a.poNumber || "—"}</span>
                          {a.held ? (
                            <Badge bg={clsMeta.tone} text={clsMeta.dark ? "dark" : undefined}>{clsMeta.label}</Badge>
                          ) : (
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{fmtMoney(a.poAmount)}</span>
                          )}
                          {a.confidence != null && (
                            <Badge bg={a.confidence >= 0.85 ? "success" : "warning"} text={a.confidence >= 0.85 ? undefined : "dark"}>
                              {a.method || "OCR"} {Math.round(Number(a.confidence) * 100)}%
                            </Badge>
                          )}
                          {a.isDuplicate ? (
                            <Badge bg="danger">{a.duplicateOf ? `Duplicate of #${a.duplicateOf}` : "Duplicate PO #"}</Badge>
                          ) : (a.poNumber && duplicatePoNums.has((a.poNumber || "").trim()) && (
                            <Badge bg="danger">Duplicate PO #</Badge>
                          ))}
                          {!a.held && units != null && (() => {
                            // POs pooled by amount cover invoices TOGETHER — name the ones each
                            // shares, not "below per-PO floor" (UI-CNV-011).
                            const shared = units > 0 ? [] : (covAtt?.sharedUnits || []);
                            const label = coverage.unitLabel || "invoice";
                            // Gap 19 (2026-10-31): the convert gate pools confirmed POs by amount
                            // (coverage.poolsByAmount), so a PO below one payment is not "below
                            // the floor" - it waits for other POs to make up a whole payment.
                            return (
                              <span style={{ fontSize: 11, color: units > 0 || shared.length ? "#166534" : (coverage.poolsByAmount ? "#92400E" : "#B91C1C") }}>
                                {units > 0
                                  ? `covers ${units} ${label}${units === 1 ? "" : "s"}`
                                  : shared.length
                                    ? `shares ${label}${shared.length === 1 ? "" : "s"} ${shared.join(", ")}`
                                      + (covAtt.sharedWith?.length ? ` with ${covAtt.sharedWith.join(", ")}` : "")
                                    : coverage.poolsByAmount
                                      ? `less than one ${label} - counts together with other POs once they make up a whole ${label}`
                                      : `below one ${label} - each PO must cover a whole ${label} on its own`}
                              </span>
                            );
                          })()}
                        </div>
                        {(a.held || a.status === "Failed") && (
                          <div style={{ fontSize: 11, color: "#92400E", marginTop: 3 }}>
                            {backendReason ? (
                              <>
                                <span style={{ fontWeight: 600 }}>{backendReason}</span>
                                {a.held && <span> — use ✎ to enter the PO # &amp; amount, or delete &amp; re-upload.</span>}
                              </>
                            ) : (
                              <>Couldn{"’"}t auto-confirm (low confidence or the part doesn{"’"}t match the quote). Use ✎ to enter the PO # &amp; amount, or delete &amp; re-upload.</>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <Row className="g-2 align-items-center">
                        <Col xs={6}>
                          <Form.Control size="sm" value={editVals.poNumber} placeholder="PO number"
                            onChange={(e) => setEditVals((v) => ({ ...v, poNumber: e.target.value }))} />
                        </Col>
                        <Col xs={4}>
                          <Form.Control size="sm" type="number" min={0} step="0.01" value={editVals.poAmount} placeholder="0.00"
                            onChange={(e) => setEditVals((v) => ({ ...v, poAmount: e.target.value }))} />
                        </Col>
                        <Col xs={2}>
                          <Button size="sm" variant="primary" onClick={() => saveEdit(a)} style={{ width: "100%" }}>Save</Button>
                        </Col>
                      </Row>
                    )}
                  </div>
                  {!editing && (
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      {/* Entitlements: 00041 correct PO, 00042 delete PO */}
                      {hasAction("00041", true) && <Button size="sm" variant="outline-secondary" title="Correct PO# / amount" onClick={() => startEdit(a)}><FaPen /></Button>}
                      {hasAction("00042", true) && <Button size="sm" variant="outline-danger" title="Delete PO" onClick={() => deleteAttachment(a)}><FaTrash /></Button>}
                    </div>
                  )}
                  {editing && <Button size="sm" variant="link" onClick={cancelEdit} style={{ flexShrink: 0 }}>Cancel</Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropzone — entitlement 00040 (upload PO attachment) */}
      {hasAction("00040", true) && (<>
      <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)} />
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploading) setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploading) setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation();
          setDragActive(false);
          if (!uploading && e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
        }}
        style={{
          border: `2px dashed ${dragActive ? "#0052A5" : "#CBD5E1"}`, borderRadius: 12, padding: 18, textAlign: "center",
          marginTop: 14, cursor: uploading ? "wait" : "pointer", background: dragActive ? "#EFF6FF" : "#fff",
          transition: "border-color .15s, background .15s",
        }}>
        {uploading ? (
          <div style={{ color: "#0052A5", fontWeight: 600, fontSize: 13 }}>
            <Spinner animation="border" size="sm" /> Uploading & extracting (OCR cascade)…
          </div>
        ) : (
          <>
            <FaCloudUploadAlt style={{ fontSize: 26, color: "#0052A5" }} />
            <div style={{ fontWeight: 600, color: "#0052A5", fontSize: 13, marginTop: 4 }}>Drop a PO PDF or click to upload</div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>PDF · PNG · JPG · direct-to-Azure · OCR runs server-side</div>
          </>
        )}
      </div>
      </>)}

      {uploadErr && <Alert variant="danger" style={{ marginTop: 12, marginBottom: 0, fontSize: 13 }}>{uploadErr}</Alert>}
    </>
  );

  // ── Step 2: Contacts ────────────────────────────────────────────────────────
  // No onSelect → a read-only display card (the contact person is fixed to the
  // primary billing contact, not picked).
  const ContactCard = ({ c, selected, onSelect, primaryFlag }) => (
    <div onClick={onSelect} className="qw-card-flat" style={{
      padding: 10, marginBottom: 8, cursor: onSelect ? "pointer" : "default",
      border: selected ? "1px solid #0052A5" : "1px solid #E2E8F0",
      background: selected ? "rgba(0,82,165,0.05)" : "#fff",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <FaUserCircle style={{ fontSize: 22, color: selected ? "#0052A5" : "#CBD5E1", flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{c.name || `Contact ${c.conNum}`}</div>
          <div style={{ fontSize: 11.5, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {[c.contactTitle || c.func, c.eMailAddress].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        {c[primaryFlag] && <Badge bg="info">Primary</Badge>}
        {selected && <FaCheckCircle style={{ color: "#0052A5", flexShrink: 0 }} />}
      </div>
    </div>
  );

  const renderContacts = () => (
    <>
      {submitErr?.onContacts && <div style={{ marginBottom: 14 }}><SubmitErrorBanner err={submitErr} /></div>}
      <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}><CoverageStrip /></div>
      {/* Ship-To LOCATION first (#4) — a real ship-to location (not a contact).
          Drives the order's ship-to, the ack CC, AND which contacts are valid as
          the Ship-To Attn below. Choosing it must come before its Attn contact. */}
      {shipTos.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Form.Label className="small mb-1">Ship-To location <span style={{ color: "#DC2626" }}>*</span></Form.Label>
          <Form.Select
            size="sm"
            value={shipToNum === null ? SHIPTO_UNSET : shipToNum}
            onChange={(e) => setShipToNum(e.target.value === SHIPTO_UNSET ? null : e.target.value)}
            style={{ maxWidth: 460 }}
          >
            <option value={SHIPTO_UNSET}>— select a ship-to —</option>
            {shipTos.map((s) => {
              const place = [s.city, s.state].filter(Boolean).join(", ");
              return (
                <option key={`st-${s.shipToNum}`} value={s.shipToNum}>
                  {s.shipToNum} · {s.name || "—"}{place ? ` (${place})` : ""}{s.primaryShipTo ? " · primary" : ""}
                </option>
              );
            })}
          </Form.Select>
          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
            {selectedShipTo?.eMailAddress
              ? `Acknowledgement CC: ${selectedShipTo.eMailAddress}`
              : "Required — select the ship-to location for this order."}
          </div>
        </div>
      )}

      {/* ── Invoice / billing recipient (customer-level; unaffected by the
             ship-to location) — spec: InvoiceRecipient_UI_Integration_Spec ── */}
      {!soldToLoading && soldToContacts.length > 0 && (
        <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Invoice / billing recipient *</div>
          {eligibleBilling.length >= 1 ? (
            <div style={{ fontSize: 13 }}>
              {eligibleBilling.length > 1 && (
                <Alert variant="warning" style={{ fontSize: 12.5 }}>
                  This customer has more than one contact flagged Primary Billing — only one should be.
                  <strong> {billingRecipient.name}</strong> is used for this order; fix the flags on the customer screen.
                </Alert>
              )}
              <div style={{ fontWeight: 700 }}>
                {billingRecipient.name} <span style={{ color: "#16A34A" }}>✓</span>
              </div>
              <div style={{ color: "#64748B", fontSize: 12.5 }}>{billingRecipient.eMailAddress}</div>
              <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 4 }}>
                This contact is the order&rsquo;s contact person — the order acknowledgement, invoices
                &amp; purchase emails for this customer go to this address. To use someone else, change
                the primary billing contact on the customer screen.
              </div>
              {eligibleBilling.length > 1 && (
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <Button size="sm" variant="outline-primary" onClick={openCustomerScreen}>Open customer screen ↗</Button>
                  <Button size="sm" variant="outline-secondary" disabled={recheckingBilling} onClick={refreshSoldToContacts}>
                    {recheckingBilling ? <Spinner animation="border" size="sm" /> : "Re-check"}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Alert variant="danger" style={{ fontSize: 12.5 }}>
                This customer has no primary billing contact (none is marked Primary Billing with a
                valid email), so the quote can&rsquo;t be converted. Open the customer screen, mark one
                contact as the primary billing contact, then come back and re-check — or{" "}
                <a href={`mailto:cs@shuklamedical.com?subject=Billing contact needed for ${quote?.custID || custNum}`}>
                  contact Customer Service
                </a>.
              </Alert>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button size="sm" variant="primary" onClick={openCustomerScreen}>Open customer screen ↗</Button>
                <Button size="sm" variant="outline-secondary" disabled={recheckingBilling} onClick={refreshSoldToContacts}>
                  {recheckingBilling ? <Spinner animation="border" size="sm" /> : "Re-check"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {soldToLoading ? (
        <div style={{ textAlign: "center", padding: 20, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading contacts…</div>
      ) : soldToContacts.length === 0 ? (
        <Alert variant="light" style={{ fontSize: 13 }}>
          No contacts loaded for this customer. You can still convert; enter the contact numbers below, or leave blank and the
          backend will use the quote's defaults (Phase 0 will block if a required contact is truly missing).
          <Row className="g-2" style={{ marginTop: 8 }}>
            <Col xs={6}><Form.Label className="small mb-1">Sold-To contact #</Form.Label><Form.Control size="sm" type="number" value={prcConNum} onChange={(e) => setPrcConNum(e.target.value)} /></Col>
            <Col xs={6}><Form.Label className="small mb-1">Ship-To contact #</Form.Label><Form.Control size="sm" type="number" value={shpConNum} onChange={(e) => setShpConNum(e.target.value)} /></Col>
          </Row>
        </Alert>
      ) : (
        <Row className="g-3">
          <Col xs={12} md={6}>
            <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Contact person</div>
            {/* Fixed to the primary billing contact — not a picker. Order,
                acknowledgement and invoices all address this person. */}
            {billingRecipient ? (
              <>
                <ContactCard c={billingRecipient} primaryFlag="primaryBilling" selected />
                <div style={{ fontSize: 11.5, color: "#94A3B8" }}>
                  The primary billing contact is the order&rsquo;s contact person.
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12.5, color: "#94A3B8", padding: "8px 2px" }}>
                Set a primary billing contact above — they become the order&rsquo;s contact person.
              </div>
            )}
          </Col>
          <Col xs={12} md={6}>
            <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Ship-To attention</div>
            {shipTos.length === 0 ? (
              // No selectable location — the customer has no ship-to address of
              // its own (or only the main-office mirror row, filtered out above).
              // Without a location there's no member list to choose from, so the
              // backend resolves the destination and its contact from the quote.
              <div style={{ fontSize: 12.5, color: "#94A3B8", padding: "8px 2px" }}>
                This customer has no separate delivery address on file — the order ships to the
                customer address and the Ship-To Attn is resolved from the quote.
              </div>
            ) : shipToNum === null ? (
              <div style={{ fontSize: 12.5, color: "#94A3B8", padding: "8px 2px" }}>Select a ship-to location first — its contacts load here.</div>
            ) : shipToContactsLoading ? (
              <div style={{ fontSize: 12.5, color: "#64748B", padding: "8px 2px" }}><Spinner animation="border" size="sm" /> Loading ship-to contacts…</div>
            ) : shipToHasNoContacts ? (
              <Alert variant="warning" style={{ fontSize: 12.5 }}>
                This delivery location has no contact on file. Add one in Epicor (Customer → Ship&nbsp;To → Contacts) or choose a different address — the order can't ship without a valid Ship-To Attn.
              </Alert>
            ) : (
              shipToContacts.map((c) => <ContactCard key={`s-${c.conNum}`} c={c} primaryFlag="primaryShipping" selected={String(shpConNum) === String(c.conNum)} onSelect={() => setShpConNum(String(c.conNum))} />)
            )}
          </Col>
        </Row>
      )}
    </>
  );

  // ── Step 3: Review + submit-for-review errors ───────────────────────────────
  const renderReview = () => {
    const sold = selectedSoldTo, shipContact = selectedShipCon;
    const shipLoc = selectedShipTo;
    return (
      <>
        <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Review</div>
          <dl style={{ margin: 0, fontSize: 13 }}>
            {[
              ["Quote", `#${quoteNum}`],
              ["Plan", `${planLabel} (${quote?.termsCode || "—"})`],
              ["POs", `${attachments.length} attached`],
              ["Contact person", sold ? sold.name : (prcConNum ? `#${prcConNum}` : "—")],
              ["Ship-To location", shipLoc ? `${shipLoc.shipToNum} · ${shipLoc.name || "—"}` : (shipToNum || "—")],
              ["Ship-To attention", shipContact ? shipContact.name : (shpConNum ? `#${shpConNum}` : "—")],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                <dt style={{ color: "#64748B", fontWeight: 400 }}>{k}</dt><dd style={{ margin: 0, fontWeight: 600 }}>{v}</dd>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", marginTop: 6, borderTop: "1px solid #EEF2F7" }}>
              <dt style={{ fontWeight: 700 }}>Grand total</dt>
              <dd style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#0052A5" }}>{fmtMoney(coverage?.totalDue ?? quote?.quoteAmt)}</dd>
            </div>
          </dl>
        </div>

        <Alert variant="primary" style={{ fontSize: 12.5 }}>
          This sends the quote and its POs to <strong>Customer Service for review</strong>. They approve it, then a background
          worker creates the Epicor order and emails the acknowledgement. You can watch the status here after submitting.
        </Alert>

        {submitErr && <SubmitErrorBanner err={submitErr} />}
      </>
    );
  };

  const SubmitErrorBanner = ({ err }) => (
    <Alert variant="danger" style={{ marginBottom: 0 }}>
      <div style={{ fontWeight: 700 }}>{err.code || "Submit failed"}</div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{err.message}</div>
      {Array.isArray(err.extra?.warnings) && err.extra.warnings.map((w, i) => (
        <div key={i} style={{ fontSize: 12.5, marginTop: 6, paddingLeft: 8, borderLeft: "3px solid #FCA5A5" }}>
          {w.code && <span className="qw-mono" style={{ fontWeight: 600 }}>{w.code}{w.message ? " — " : ""}</span>}
          {w.message}
        </div>
      ))}
    </Alert>
  );

  // ── Step 4: Status tracker (maker-checker lifecycle) ────────────────────────
  // Submitted quotes move PendingReview → Approved → Creating → OrderCreated (or
  // ChangesRequested / CreationFailed). The polling effect above keeps `review`
  // fresh while it's in flight; this just renders the latest snapshot.
  const renderStatus = () => {
    const r = review || {};
    const color = statusColor(r.statusKind);
    const stage = Math.max(0, Math.min(5, Number(r.stage) || 0));
    const inFlight = NON_TERMINAL_STATUSES.includes(r.status);
    return (
      <div>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: 999, background: color, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", marginBottom: 8 }}>
            {r.status === "OrderCreated" ? <FaCheckCircle style={{ fontSize: 26 }} />
              : r.status === "CreationFailed" ? <FaExclamationTriangle style={{ fontSize: 24 }} />
              : inFlight ? <Spinner animation="border" />
              : <FaRegClock style={{ fontSize: 22 }} />}
          </div>
          <div className="qw-eyebrow" style={{ color }}>{r.statusLabel || r.status || "Submitted"}</div>
          <div style={{ fontSize: 12.5, color: "#64748B", marginTop: 2 }}>Quote #{quoteNum} · {fmtMoney(coverage?.totalDue ?? quote?.quoteAmt)}</div>
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n} style={{ flex: 1, height: 5, borderRadius: 999, background: n <= stage ? color : "#E2E8F0" }} />
          ))}
        </div>

        {r.status === "OrderCreated" && (
          <div className="qw-card-flat qw-pad" style={{ textAlign: "center", marginBottom: 12 }}>
            <div className="qw-eyebrow" style={{ color: "#10B981" }}>Sales order</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#0F172A" }}>#{r.orderNum}</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, padding: "5px 12px", borderRadius: 999, background: "#FEF3C7", color: "#92400E", fontSize: 12, fontWeight: 600 }}>
              <FaRegClock /> Acknowledgement scheduled — typically within a minute
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <Button size="sm" variant="primary" style={{ fontWeight: 600 }}
                onClick={() => { onHide?.(); navigate(`/order/${encodeURIComponent(r.orderNum)}`); }}>
                <FaFileInvoiceDollar style={{ marginRight: 6 }} /> View order <FaArrowRight style={{ marginLeft: 6 }} />
              </Button>
              <Dropdown>
                <Dropdown.Toggle size="sm" variant="outline-secondary" disabled={downloading} style={{ fontWeight: 600 }}>
                  <FaDownload style={{ marginRight: 6 }} /> {downloading ? "Preparing…" : "Download quote"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {REPORT_STYLES.map((s) => (
                    <Dropdown.Item key={s.value} onClick={() => handleDownload(s.value)}>{s.label}</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {downloadErr && <div style={{ fontSize: 11.5, color: "#DC2626", marginTop: 6 }}>{downloadErr}</div>}
            </div>
          </div>
        )}

        {r.status === "ChangesRequested" && (
          <Alert variant="warning" style={{ fontSize: 13 }}>
            <strong>Returned for changes.</strong>{r.returnReason ? ` ${r.returnReason}` : ""} Fix the POs and re-submit.
          </Alert>
        )}

        {/* No reason = cleared by a PO delete (the old reason described the old PO set). */}
        {r.status === "CreationFailed" && (r.creationError ? (
          <Alert variant="danger" style={{ fontSize: 13 }}>
            <strong>Order creation failed.</strong> {r.creationError} Customer Service / ops will retry.
          </Alert>
        ) : (
          <Alert variant="info" style={{ fontSize: 13 }}>
            The POs changed after the last order-creation attempt failed. Customer Service / ops will retry.
          </Alert>
        ))}

        {inFlight && (
          <div style={{ fontSize: 12.5, color: "#64748B", textAlign: "center", marginBottom: 12 }}>
            {r.status === "PendingReview" ? "Waiting for Customer Service to review."
              : r.status === "Approved" ? "Approved — the worker will create the order shortly."
              : "Creating the order in Epicor…"} This page updates automatically.
          </div>
        )}

        {r.systemCheckDetail && (
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
            <Badge bg={r.systemCheck === "Ready" ? "success" : r.systemCheck === "Hold" ? "warning" : "secondary"} text={r.systemCheck === "Hold" ? "dark" : undefined} style={{ marginRight: 6 }}>{r.systemCheck}</Badge>
            {r.systemCheckDetail}
          </div>
        )}

        {Array.isArray(r.reviewerChain) && r.reviewerChain.length > 0 && (
          <div className="qw-card-flat qw-pad">
            <div className="qw-eyebrow" style={{ marginBottom: 8 }}>History</div>
            {r.reviewerChain.map((s) => (
              <div key={s.step} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "2px 0" }}>
                <span style={{ color: "#0F172A" }}>{s.actionLabel || s.action} <span style={{ color: "#94A3B8" }}>· {shortActor(s.actor)}</span></span>
                <span className="qw-mono" style={{ color: "#94A3B8" }}>{fmtWhen(s.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Footer buttons per step ──────────────────────────────────────────────────
  const Footer = () => {
    // On mobile each button spans the row (full-width, easy thumb targets);
    // on desktop they sit right-aligned at their natural width.
    const btn = isMobile ? { fontWeight: 600, flex: "1 1 100%" } : { fontWeight: 600 };
    if (step === 3) {
      // Hand the toast to the parent — it closes the modal and reloads the quote
      // (now PendingReview, or ordered once the worker finishes). Belt-and-suspenders onHide.
      const done = () => { if (successMsg) onConverted?.(successMsg); onHide?.(); };
      return <Button variant="primary" onClick={done} style={btn}>Done</Button>;
    }
    return (
      <>
        <Button variant="outline-secondary" style={isMobile ? { flex: "1 1 100%" } : undefined}
          onClick={step === 0 ? onHide : () => setStep((s) => s - 1)} disabled={submitting}>
          {step === 0 ? "Cancel" : <><FaArrowLeft style={{ marginRight: 6 }} /> Back</>}
        </Button>
        {step < 2 ? (
          <Button variant="primary" style={btn}
            disabled={uploading || (step === 0 && !canLeaveUpload) || (step === 1 && !canLeaveContacts)}
            onClick={() => {
              // Leaving Contacts after a refusal: the rep has fixed it (or re-checked),
              // so the old refusal must not linger on Review (UI-CNV-033).
              if (step === 1) setSubmitErr(null);
              setStep((s) => s + 1);
            }}>
            {step === 0 && !canLeaveUpload ? "PO coverage required"
              : step === 1 && !canLeaveContacts
                ? (billingOk ? "Contact & ship-to required" : "Billing recipient required")
              : <>Next <FaArrowRight style={{ marginLeft: 6 }} /></>}
          </Button>
        ) : hasAction("00043", true) ? (
          <Button variant="success" style={btn} onClick={submitForReview} disabled={submitting}>
            {submitting ? <><Spinner animation="border" size="sm" /> Submitting…</> : <><FaFileInvoiceDollar style={{ marginRight: 6 }} /> Submit for Review</>}
          </Button>
        ) : (
          <Button variant="success" style={btn} disabled title="Your role is not entitled to submit for review">
            <FaFileInvoiceDollar style={{ marginRight: 6 }} /> Submit for Review
          </Button>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} centered size="lg"
      fullscreen="sm-down" backdrop={submitting ? "static" : true}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title style={{ fontSize: 18 }}>
          {step === 3 ? "Conversion status" : `Submit quote ${quoteNum} for conversion`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step !== 3 && <StepRail />}
        {step === 0 && renderUpload()}
        {step === 1 && renderContacts()}
        {step === 2 && renderReview()}
        {step === 3 && renderStatus()}
      </Modal.Body>
      <Modal.Footer><Footer /></Modal.Footer>
    </Modal>
  );
}

export default ConvertToOrderModal;
